/* File: frontend/src/components/reputation/AnalyticsView.js */
/* Version: 4.1 (Real Data Only - No Lies) */
/* Date: 2025-12-13 20:54 EST */
/* Description: Removed mock data fallback. Added honest empty state handling. Restored profile prop passing. */

import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw, TrendingUp, AlertTriangle } from 'lucide-react';
import AIHealthWidget from './AIHealthWidget';
import DetailedAuditReport from './DetailedAuditReport';
import TruthAttackDashboard from './TruthAttackDashboard';
import apiService from '../../services/apiService';

// --- COLORS ---
const getColorForLabel = (label, index) => {
    // [FIX] Defensive coding: label might be undefined from backend
    const lower = (label || '').toLowerCase();
    // Semantic colors based on keywords
    if (lower.includes('accurate') || lower.includes('positive') || lower.includes('win') || lower.includes('good')) return '#10b981'; // Green
    if (lower.includes('missing') || lower.includes('neutral') || lower.includes('unknown')) return '#e5e7eb'; // Gray
    if (lower.includes('hallucination') || lower.includes('negative') || lower.includes('loss') || lower.includes('risk')) return '#ef4444'; // Red

    // Fallback Palette
    const palette = ['#C8A653', '#f59e0b', '#8b5cf6', '#ec4899'];
    return palette[index % palette.length];
};

// --- TRANSFORMER ---
const transformToReportFormat = (backendData) => {
    if (!backendData) return null;

    // Честный расчет, без заглушек
    const rawScore = backendData.summary?.win_rate_global;
    const score = (rawScore !== null && rawScore !== undefined) ? Math.min(Math.round(rawScore), 100) : null;

    const distribution = (backendData.pie_data || []).map((item, index) => ({
        name: item.name,
        value: item.value,
        color: getColorForLabel(item.name, index)
    }));

    const history = (backendData.chart_data || []).map(item => ({
        date: item.date,
        score: item.win_rate
    }));

    return {
        score,
        distribution,
        history,
        action_plan: backendData.action_plan || []
    };
};

const AnalyticsView = ({ profile, onFixRequest }) => {
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEmpty, setIsEmpty] = useState(false);

    // Используем company_url или website_url
    const targetAuditUrl = profile?.company_url || profile?.website_url;
    const abortControllerRef = useRef(null);

    useEffect(() => {
        const loadAnalytics = async () => {
            if (!profile?.id) return;

            if (abortControllerRef.current) abortControllerRef.current.abort();
            abortControllerRef.current = new AbortController();

            setLoading(true);
            setIsEmpty(false);

            try {
                // Запрашиваем статистику для КОНКРЕТНОГО профиля
                const jsonData = await apiService.makeRequest(
                    `/reputation/analytics/summary?days=30&profile_id=${profile.id}`
                );

                // Если битв нет или summary пустой (win_rate_global === null) - показываем честное "пусто"
                if (!jsonData.summary || jsonData.summary.total_battles === 0 || jsonData.summary.win_rate_global === null) {
                    setIsEmpty(true);
                    setReportData(null);
                } else {
                    setReportData(transformToReportFormat(jsonData));
                }
            } catch (e) {
                if (e.name === 'AbortError') return;
                console.error("Analytics fetch error", e);
                setIsEmpty(true);
            } finally {
                if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
                    setLoading(false);
                }
            }
        };

        loadAnalytics();
        return () => { if (abortControllerRef.current) abortControllerRef.current.abort(); };
    }, [profile]);

    if (loading) return (
        <div className="state-message">
            <RefreshCw size={24} className="spin-anim" style={{ marginBottom: 10 }} />
            Analyzing Real Data...
        </div>
    );

    return (
        <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* TIER 1: TECHNICAL VISIBILITY (Full Width Widget) */}
            <div style={{ width: '100%' }}>
                {targetAuditUrl ? (
                    <AIHealthWidget targetUrl={targetAuditUrl} autoRun={true} />
                ) : (
                    <div className="ui-card">
                        <div className="ui-card-header" style={{ color: '#0f172a' }}>
                            <TrendingUp size={16} className="text-blue-500" /> AI VISIBILITY SCORE
                        </div>
                        <div style={{ padding: '12px', background: '#fff7ed', color: '#c2410c', borderRadius: '6px', border: '1px solid #fed7aa', fontSize: '12px' }}>
                            ⚠️ Please save a <b>Website URL</b> in settings to enable visibility scanning.
                        </div>
                    </div>
                )}
            </div>

            {/* TIER 2.5: TRUTH VS ATTACK (New Dashboard) */}
            <div style={{ width: '100%' }}>
                <TruthAttackDashboard profileId={profile?.id} />
            </div>

            {/* TIER 3: SEMANTIC REPUTATION HEALTH (Detailed Report) */}
            <div className="ui-card" style={{ width: '100%', padding: '0', border: 'none', boxShadow: 'none', background: 'transparent' }}>
                {/* Header is handled inside DetailedAuditReport usually, or we can wrap it here. 
                     Looking at Image 2, "Reputation Health & Strategy" is a header above the big card.
                 */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
                    <TrendingUp size={20} className="text-emerald-500" /> Reputation Health & Strategy
                </div>

                {isEmpty ? (
                    <div className="ui-card" style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>
                        <AlertTriangle size={48} style={{ margin: '0 auto 16px', color: '#eab308' }} />
                        <h3 style={{ color: '#1e293b', fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>No Battle Data Yet</h3>
                        <p style={{ maxWidth: '400px', margin: '0 auto', fontSize: '14px' }}>
                            We need to run at least one Agent Battle to analyze your semantic reputation.
                            Start a battle in the "Agent Battle" tab.
                        </p>
                    </div>
                ) : (
                    <DetailedAuditReport
                        profile={profile}
                        reportData={reportData}
                        onExecuteFix={onFixRequest}
                    />
                )}
            </div>
        </div>
    );
};

export default AnalyticsView;